import { Box } from "@mui/material";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  INSIGHT_PANEL_CONTAINER_SX,
  insightPanelContentPaddingSx,
  insightPanelScrollSx,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";

interface InsightPanelScrollBoxProps {
  accentColor: string;
  children: ReactNode;
}

/**
 * Scroll interno só quando o conteúdo excede o painel — roda do mouse propaga ao `<main>` quando não há overflow.
 */
export function InsightPanelScrollBox({
  accentColor,
  children,
}: InsightPanelScrollBoxProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      setNeedsScroll(el.scrollHeight > el.clientHeight + 2);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true, attributes: true });

    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        ...INSIGHT_PANEL_CONTAINER_SX,
        ...insightPanelContentPaddingSx(),
        ...(needsScroll
          ? insightPanelScrollSx(accentColor)
          : {
              overflowX: "hidden",
              overflowY: "visible",
            }),
      }}
    >
      {children}
    </Box>
  );
}
